import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  IconButton,
  List,
  ListItem,
  Collapse,
  Alert,
  useTheme,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import OrderStatusChip from "./OrderStatusChip";
import DeliveryDetails from "../../../../Components/DeliveryDetails";
import TimerIcon from "@mui/icons-material/Timer";

const OrderItem = ({ order, expanded }) => {
  console.log("Orders check test",order)
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(expanded);

  useEffect(() => {
    setShowDetails(expanded);
  }, [expanded]);

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const hasExpandableDetails =
    order.items?.length > 0 ||
    order.notes ||
    order.order_reason ||
    order.signature_files?.length > 0 ||
    order.other_files?.length > 0;

  const orderNoColor =
    (order.is_return_job === 1 && order.return_job_color) ||
    (order.is_partial_job === 1 && order.partial_job_color) ||
    theme.palette.primary.main;

  const orderColorLabel =
    order.is_return_job === 1 && order.return_job_color
      ? "this color represents returned order"
      : order.is_partial_job === 1 && order.partial_job_color
      ? "this color represents partial delivery"
      : null;

  return (
    <Paper elevation={1} sx={{ mb: 1, borderRadius: 2 }}>
      <Box sx={{ p: 2 }}>
        {orderColorLabel && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "2px",
                backgroundColor: orderNoColor,
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
            <Typography sx={{ fontSize: "0.8rem", color: "#667085" }}>
              {orderColorLabel}
            </Typography>
          </Box>
        )}

        {/* Header with Order Number and Status */}
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Grid item>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <ReceiptIcon
                  sx={{ fontSize: 16 }}
                  style={{ color: orderNoColor }}
                />
              </Grid>
              <Grid item>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ mt: "-4px" }}
                  style={{ color: orderNoColor }}
                >
                  {order.orderNo}
                </Typography>
              </Grid>
              {order.priority === "urgent" && (
                <Grid item>
                  <Chip
                    label="URGENT"
                    size="small"
                    color="error"
                    variant="filled"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <OrderStatusChip status={order.status} />
          </Grid>
        </Grid>

        {/* Main Content - Customer & Order Details */}
        <Grid container spacing={3}>
          {/* Customer Details Section */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "grey.50",
                p: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
              >
                <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  textTransform="uppercase"
                  color="text.secondary"
                >
                  Customer Details
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {order.customerName}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <BusinessIcon
                    sx={{ fontSize: 14, color: "text.secondary" }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Code:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {order.customerCode}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    Phone:
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {order.customerPhone}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Order Details Section */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: "grey.50",
                p: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.200",
                height: "100%",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
              >
                <AccessTimeIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  textTransform="uppercase"
                  color="text.secondary"
                >
                  Timeline
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {order.orderDate && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: "info.main" }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 65 }}
                    >
                      Order Date:
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>
                      {order.orderDate}
                    </Typography>
                  </Box>
                )}

                {order.loadedTime && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocalShippingIcon
                      sx={{ fontSize: 14, color: "warning.main" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 65 }}
                    >
                      Loaded Date:
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>
                      {order.loadedTime}
                    </Typography>
                  </Box>
                )}
                {order.deliveryStartTime && (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <HourglassEmptyIcon sx={{ fontSize: 14, color: "warning.main" }} />
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 65 }}>
      Delivery Start:
    </Typography>
    <Typography variant="caption" fontWeight={500}>
      {order.deliveryStartTime}
    </Typography>
  </Box>
)}
                {order.deliveredTime && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircleIcon
                      sx={{ fontSize: 14, color: "success.main" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 65 }}
                    >
                      Delivered Date:
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>
                      {order.deliveredTime}
                    </Typography>
                  </Box>
                )}

                 {order.totalDeliveryTime && (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <TimerIcon sx={{ fontSize: 14, color: "warning.main" }} />
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 65 }}>
      Total Delivery Time:
    </Typography>
    <Typography variant="caption" fontWeight={500}>
      {order.totalDeliveryTime}
    </Typography>
  </Box>
)}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {showDetails && hasExpandableDetails && (
          <Collapse in={showDetails}>
            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #eee" }}>
              {order.items && order.items.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    fontWeight="medium"
                    color="text.secondary"
                  >
                    Items
                  </Typography>
                  {/* Column Headers */}
                  <Box sx={{ width: "100%", mt: 1, mb: 0.5 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography
                          variant="caption"
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          Stock Code
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant="caption"
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          Description
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography
                          variant="caption"
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          Qty
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography
                          variant="caption"
                          fontWeight="medium"
                          color="text.secondary"
                        >
                          Remarks
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  {/* Items List */}
                  <List dense>
                    {order.items.map((item, i) => (
                      <ListItem
                        key={i}
                        disableGutters
                        sx={{
                          py: 1,
                          px: 1,
                          borderBottom:
                            i < order.items.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                          ...(item.colorState && {
                            backgroundColor: item.colorState,
                          }),
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <Typography variant="body2" fontWeight="medium">
                                {item.code}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography variant="body2">
                                {item.description}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="body2" fontWeight="medium">
                                {item.quantity}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="body2">
                                {item.remarks || "-"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Delivery Details Section */}
              <DeliveryDetails data={order} />

              {order.status?.toLowerCase() === "failed" && order.order_reason && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Reason: {order.order_reason}
                </Alert>
              )}

              {order.notes && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Note: {order.notes}
                </Alert>
              )}
            </Box>
          </Collapse>
        )}

        {expanded && hasExpandableDetails && (
          <Box sx={{ mt: 1, textAlign: "right" }}>
            <IconButton
              size="small"
              onClick={toggleDetails}
              sx={{
                transform: showDetails ? "rotate(180deg)" : "none",
                transition: "transform 0.3s ease-in-out",
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default OrderItem;
